import { DEFAULT_THRESHOLDS } from './signalEngine';
import { createTouchEngine, TOUCH_DEFAULTS } from './activityEngine';

export const CALIBRATION_STORAGE_KEY = 'spore.calibration.v1';

export const CALIBRATION_STEPS = [
  { id: 'still', title: 'Do not touch the specimen', detail: 'Hands away. Measuring the noise floor and resting baseline.', seconds: 6 },
  { id: 'gentle', title: 'Stroke gently', detail: 'Stroke it the way the audience will — including firmer strokes.', seconds: 10 },
  { id: 'tap', title: 'Tap firmly 3–5 times', detail: 'Distinct, firm taps with a short pause between each.', seconds: 7 },
];

export const TOUCH_CALIBRATION_STEPS = [
  { id: 'still', title: 'Hands off — learning base state', detail: 'Nobody touch the specimen. Learning its background flutter.', seconds: 12 },
  { id: 'taps', title: 'A few quick taps', detail: 'Four or five short, quick taps with pauses in between.', seconds: 8 },
  { id: 'stroke', title: 'One long slow stroke', detail: 'One continuous, slow stroke for most of the countdown.', seconds: 8 },
];

// Replays the three recorded steps through a fresh touch detector and derives baseline + duration boundaries.
// Feed the three phases through a fresh activity engine: hands-off -> baseline; taps/stroke -> activity levels.
export function computeTouchCalibration({ still = [], taps = [], stroke = [] }, defaults = TOUCH_DEFAULTS) {
  const eng = createTouchEngine({ ...defaults, warmupMs: 0 }, { startT: still.length ? still[0].t : 0 });
  const acts = { taps: [], stroke: [] };
  const feed = (events, key) => {
    let i = 0;
    if (!events.length) return;
    for (let t = events[0].t; t <= events[events.length - 1].t; t += 20) {
      while (i < events.length && events[i].t <= t) eng.ingest(events[i++]);
      const st = eng.tick(t);
      if (key) acts[key].push(st.activity);
    }
  };
  if (still.length) eng.learnBaseline(still[still.length - 1].t - still[0].t, still[0].t);
  feed(still, null);
  feed(taps, 'taps');
  feed(stroke, 'stroke');
  const baseline = eng.getBaseline();
  const strokeMed = median(acts.stroke);
  const tapPeak = acts.taps.length ? Math.max(...acts.taps) : 0;
  const warnings = [];
  const config = { thresholdScale: 1 };
  if (strokeMed < 0.1 && tapPeak < 0.2) warnings.push('Touch barely differs from hands-off on this specimen: defaults kept. Try sensitivity 4-5 or check the electrodes.');
  else config.thresholdScale = Math.round(Math.min(2.5, Math.max(0.4, strokeMed / 0.5 || 1)) * 100) / 100;
  return {
    ok: !warnings.length,
    warnings,
    baseline,
    config,
    rows: [
      ['Baseline motion (median)', baseline.median.raw.toFixed(1)],
      ['Baseline spread', baseline.spread.toFixed(1)],
      ['Tap peak activity', tapPeak.toFixed(2)],
      ['Stroke median activity', strokeMed.toFixed(2)],
      ['Threshold scale', config.thresholdScale],
    ],
  };
}

const median = (a) => {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const percentile = (a, p) => {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  return s[Math.min(s.length - 1, Math.floor(p * s.length))];
};
const topMedian = (a, n) => median([...a].sort((x, y) => y - x).slice(0, n));

const ccEvents = (arr, ccNumber) => arr.filter((e) => e.type === 'cc' && e.cc === ccNumber);
const velocities = (arr) => arr.filter((e) => e.type === 'note_on' && e.velocity > 0).map((e) => e.velocity);

function ccJumps(events, windowMs, startValue) {
  const jumps = [];
  let prev = startValue;
  events.forEach((e, i) => {
    let lo = prev;
    for (let j = i - 1; j >= 0 && e.t - events[j].t <= windowMs; j -= 1) lo = Math.min(lo, events[j].value);
    jumps.push(e.value - lo);
    prev = e.value;
  });
  return jumps;
}

export function computeCalibration({ still = [], gentle = [], tap = [] }, defaults = DEFAULT_THRESHOLDS) {
  const d = { ...defaults };
  const warnings = [];
  const ccN = d.ccNumber;

  const ccStill = ccEvents(still, ccN).map((e) => e.value);
  const baseline = ccStill.length ? Math.round(median(ccStill)) : d.ccBaseline;
  let noise = d.ccNoiseFloor;
  if (ccStill.length) {
    const maxDev = Math.max(...ccStill.map((v) => Math.abs(v - baseline)));
    const mean = ccStill.reduce((a, v) => a + v, 0) / ccStill.length;
    const std = Math.sqrt(ccStill.reduce((a, v) => a + (v - mean) ** 2, 0) / ccStill.length);
    noise = Math.min(30, Math.max(3, Math.ceil(maxDev + 1), Math.ceil(3 * std)));
  }
  const stillVel = velocities(still);
  const noiseVelocity = stillVel.length ? Math.min(60, Math.max(d.noiseVelocity, Math.max(...stillVel) + 1)) : d.noiseVelocity;
  if (stillVel.length > 10) warnings.push(`Specimen produced ${stillVel.length} notes while untouched — noisy. Note noise gate raised to velocity ${noiseVelocity}.`);

  const gentleVel = velocities(gentle);
  const gentleCc = ccEvents(gentle, ccN);
  const tapVel = velocities(tap);
  const tapCc = ccEvents(tap, ccN);
  if (!gentleVel.length && !gentleCc.length) warnings.push('No signal received during the gentle stroke step.');
  if (!tapVel.length && !tapCc.length) warnings.push('No signal received during the firm tap step.');

  const gentleVelP90 = gentleVel.length ? percentile(gentleVel, 0.9) : 0;
  const tapPeakVel = tapVel.length ? topMedian(tapVel, 5) : 0;
  const gentleJumps = gentleCc.length ? ccJumps(gentleCc, d.pulseCcWindowMs, baseline) : [];
  const gentleJump = gentleJumps.length ? percentile(gentleJumps, 0.95) : 0;
  const gentleVelMax = gentleVel.length ? percentile(gentleVel, 0.95) : d.gentleVelMax;
  const gentleCcJumpMax = gentleJumps.length ? Math.max(3, percentile(gentleJumps, 0.98)) : d.gentleCcJumpMax;
  const tapJump = tapCc.length ? topMedian(ccJumps(tapCc, d.pulseCcWindowMs, baseline), 3) : 0;

  const notesSeparate = tapVel.length >= 3 && tapPeakVel - Math.max(gentleVelP90, noiseVelocity) >= 15;
  const ccSeparate = tapCc.length > 0 && tapJump - gentleJump >= 15 && tapJump >= gentleJump * 1.5;

  let pulseVelocity = d.pulseVelocity;
  let pulseCcJump = d.pulseCcJump;
  if (notesSeparate) pulseVelocity = Math.round(Math.min(127, Math.max(noiseVelocity + 5, (tapPeakVel + Math.max(gentleVelP90, noiseVelocity)) / 2)));
  if (ccSeparate) pulseCcJump = Math.round(Math.max(noise * 2, (tapJump + gentleJump) / 2));

  if (!notesSeparate && !ccSeparate) {
    warnings.push('Firm taps did not clearly separate from gentle strokes. Kept safe default pulse thresholds — consider redoing calibration.');
    pulseVelocity = d.pulseVelocity;
    pulseCcJump = d.pulseCcJump;
  } else {
    if (!notesSeparate && gentleVelP90 >= pulseVelocity) {
      pulseVelocity = 128;
      warnings.push('Note velocities did not separate; note-based pulses disabled (CC90 jumps only).');
    }
    if (!ccSeparate && gentleJump >= pulseCcJump) {
      pulseCcJump = 999;
      warnings.push('CC90 jumps did not separate; CC-based pulses disabled (note velocity only).');
    }
  }

  const allCc = [...gentleCc, ...tapCc].map((e) => e.value);
  const ccMax = allCc.length ? Math.max(baseline + noise + 20, Math.max(...allCc)) : d.ccMax;
  const gentleRate = (gentleVel.length + gentleCc.length) / CALIBRATION_STEPS[1].seconds;
  const strokeMinEvents = gentleRate > 0 ? Math.min(d.strokeMinEvents, Math.max(2, Math.floor(gentleRate * 0.5))) : d.strokeMinEvents;

  const thresholds = {
    ...d,
    ccBaseline: baseline,
    ccNoiseFloor: noise,
    ccMax: Math.min(127, ccMax),
    noiseVelocity,
    pulseVelocity,
    pulseCcJump,
    strokeMinEvents,
    gentleVelMax,
    gentleCcJumpMax,
  };
  return {
    ok: notesSeparate || ccSeparate,
    thresholds,
    warnings,
    summary: {
      stillCcSamples: ccStill.length,
      stillNotes: stillVel.length,
      gentleNotes: gentleVel.length,
      gentleCcSamples: gentleCc.length,
      gentleVelP90,
      gentleCcJumpP95: gentleJump,
      tapNotes: tapVel.length,
      tapCcSamples: tapCc.length,
      tapPeakVelocity: tapPeakVel,
      tapCcJump: tapJump,
      notesSeparate,
      ccSeparate,
    },
  };
}

export function saveCalibration(result, storage = window.localStorage) {
  const record = { ...result, calibratedAt: new Date().toISOString() };
  storage.setItem(CALIBRATION_STORAGE_KEY, JSON.stringify(record));
  return record;
}

export function loadCalibration(storage = window.localStorage) {
  try {
    const raw = storage.getItem(CALIBRATION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearCalibration(storage = window.localStorage) {
  storage.removeItem(CALIBRATION_STORAGE_KEY);
}
